import React from 'react';
import {
  deleteApprovalKeywordUsingBody,
  useRecentReadApproval,
} from '@/hooks/apiHooks';
import { t } from 'i18next';
import { Approval, RecommendFilterResponse, SearchData } from '@/types/api';
import { KeyedMutator } from 'swr';
import RequestSearch from '@/components/Approval/RightContainer/SearchApproval/RequestSearch';
import { deleteApprovalRecentlyViewed } from '@/hooks/apiHooks/legacyApiHooks';
import { Stack, Typography } from '@mui/material';
import RecentViewedItem from '@/components/Work/WorkSNB/SNBRecent/RecentViewedItem';
import StackWithDivider from '@/components/StackWithDivider';

type P = {
  handleOpen: (id: string) => void;
  handleSearch: ({ keyword }: Pick<SearchData, 'keyword'>) => void;
  keywordData?: string[];
  recentSearchData?: Approval[];
  mutate: KeyedMutator<RecommendFilterResponse>;
  focusedIndex: number;
};

const InitSearchView = ({
  keywordData,
  recentSearchData,
  mutate,
  handleSearch,
  handleOpen,
  focusedIndex,
}: P) => {
  const { mutate: recentReadApprovalMutate } = useRecentReadApproval();
  const haveKeywordData = keywordData && keywordData.length > 0;
  const haveRecentSearchData = recentSearchData && recentSearchData.length > 0;
  const handleDeleteKeyword = (keyword: string) => {
    deleteApprovalKeywordUsingBody(keyword).then(() => mutate());
  };
  const handleDelete = (docId: string) => {
    deleteApprovalRecentlyViewed(docId).then(() => recentReadApprovalMutate());
  };

  return (
    <StackWithDivider gap={'16px'} sx={{ padding: '16px 12px' }}>
      {/* 최근 검색어, 최근 본 문서가 없을 때 */}
      {!haveKeywordData && !haveRecentSearchData && (
        <RequestSearch
          title={t('찾고자 하는 품의를 검색해 주세요.')}
          isLoading={false}
        />
      )}

      {/* 최근 검색어 */}
      {haveKeywordData && (
        <Stack direction={'column'} gap={'8px'}>
          <Typography fontWeight={500} fontSize={'12px'} color={'var(--dk-text-secondary, #888d96)'}>
            {t('최근 검색어')}
          </Typography>
          <Stack gap={'4px'} flex={1}>
            {keywordData.map((item, index) => (
              <RecentViewedItem
                key={`historySearch-${index}`}
                id={item}
                title={item}
                onClick={() => handleSearch({ keyword: item })}
                onDeleteClick={() => handleDeleteKeyword(item)}
                isFocused={focusedIndex === index}
              />
            ))}
          </Stack>
        </Stack>
      )}

      {/* 최근 본 문서 */}
      {haveRecentSearchData && (
        <Stack direction={'column'} gap={'8px'}>
          <Typography fontWeight={500} fontSize={'12px'} color={'var(--dk-text-secondary, #888d96)'}>
            {t('최근 본 문서')}
          </Typography>
          <Stack gap={'4px'} flex={1}>
            {recentSearchData.map((item, index) => (
              <RecentViewedItem
                key={item.docId}
                id={item.docId}
                title={item.subject}
                authorName={item.author.name}
                authorPosition={item.author.positionName}
                viewedDate={item.authoredAt || item.createdAt}
                onClick={() => handleOpen(item.docId)}
                onDeleteClick={() => handleDelete(item.docId)}
                isFocused={focusedIndex === index + (keywordData?.length ?? 0)}
              />
            ))}
          </Stack>
        </Stack>
      )}
    </StackWithDivider>
  );
};

export default InitSearchView;
